/**
 * Copyright 2025 John Huikku Alienrobot LLC
 * www.ALIENROBOT.com
 */

import { Check, Copy, FileDown, Save, ArrowLeft, Maximize2 } from 'lucide-react'
import { JudgeLogo } from '@/ui/components/LLMLogos'
import type { JudgeResult, PeerResponse } from '@/ui/stores/orchestrator-store'
import type { ChatMessage } from '@/lib/storage'
import { OPENROUTER_MODELS } from '@/providers/llm-provider'

interface JudgePanelProps {
  height: number
  judgeModel: string | undefined
  judgeResult: JudgeResult | null
  judgeAutoSend: boolean
  judgeThreshold: number
  judgeNotes: string
  isEditingThreshold: boolean
  approvalSent: boolean
  isOrchestrating: boolean
  peerResponses: Record<string, PeerResponse | null>
  messages: ChatMessage[]
  projectName: string | undefined
  onToggleAutoSend: () => void
  onSetThreshold: (threshold: number) => void
  onSetNotes: (notes: string) => void
  onSetEditingThreshold: (editing: boolean) => void
  onApprove: () => Promise<void>
  onRequestRevisions: () => void
  onCopy: () => void
  onInflate: () => void
  onSave: () => void
  onSendToOrchestrator: () => void
  onExpand: () => void
  onResizeStart: () => void
}

export function JudgePanel({
  height,
  judgeModel,
  judgeResult,
  judgeAutoSend,
  judgeThreshold,
  judgeNotes,
  isEditingThreshold,
  approvalSent,
  isOrchestrating,
  peerResponses,
  onToggleAutoSend,
  onSetThreshold,
  onSetNotes,
  onSetEditingThreshold,
  onApprove,
  onRequestRevisions,
  onCopy,
  onInflate,
  onSave,
  onSendToOrchestrator,
  onExpand,
  onResizeStart,
}: JudgePanelProps) {
  // Get model display name
  const getModelName = (modelId: string | undefined): string => {
    if (!modelId) return ''
    const model = OPENROUTER_MODELS.find(m => m.id === modelId)
    return model ? model.name : modelId
  }

  // Check if all peers have responded
  const allPeersResponded = Object.values(peerResponses).every(r => r !== null)

  return (
    <div style={{ height: `${height}px` }} className="bento-tile flex flex-col">
      <div className="bento-tile-header flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <JudgeLogo className="w-4 h-4 text-yellow-400" />
          <div className="flex flex-col items-start">
            <span className="text-xs font-semibold">Judge</span>
            <span className="text-[10px] text-foreground-muted">{getModelName(judgeModel)}</span>
          </div>
        </div>

        <div className="flex items-center gap-2 text-[10px]">
          <div className="flex items-center gap-1">
            <span>Auto-send</span>
            <button
              onClick={onToggleAutoSend}
              className={`relative inline-flex h-3 w-6 items-center rounded-full transition-colors ${
                judgeAutoSend ? 'bg-accent' : 'bg-background-tertiary'
              }`}
              title={judgeAutoSend ? 'Disable auto-send' : 'Enable auto-send'}
            >
              <span
                className={`inline-block h-2 w-2 transform rounded-full bg-white transition-transform ${
                  judgeAutoSend ? 'translate-x-3' : 'translate-x-0.5'
                }`}
              />
            </button>
          </div>

          <span className="text-foreground-muted">|</span>

          <div className="flex items-center gap-1">
            <span>Threshold:</span>
            {isEditingThreshold ? (
              <input
                type="number"
                min="0"
                max="1"
                step="0.05"
                value={judgeThreshold}
                onChange={(e) => onSetThreshold(Number.parseFloat(e.target.value) || 0.90)}
                onBlur={() => onSetEditingThreshold(false)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') onSetEditingThreshold(false)
                }}
                className="w-12 px-1 bg-background-tertiary border border-accent rounded text-[10px]"
                autoFocus
              />
            ) : (
              <span
                onClick={() => onSetEditingThreshold(true)}
                className="cursor-pointer hover:text-accent underline decoration-dotted"
              >
                {judgeThreshold.toFixed(2)}
              </span>
            )}
          </div>
        </div>

        <div className="ml-auto flex items-center gap-1">
          {isOrchestrating && !judgeResult && allPeersResponded && (
            <span className="text-accent animate-pulse">●</span>
          )}

          {judgeResult && !approvalSent && (
            <>
              <button
                onClick={onRequestRevisions}
                className="px-2 py-1 bg-orange-500/20 text-orange-400 hover:bg-orange-500/30 rounded transition-colors text-[10px] font-bold"
                title="Send judge's feedback to orchestrator for revisions - orchestrator will update the work based on this assessment"
              >
                <ArrowLeft className="w-3 h-3 inline mr-1" />
                REQUEST REVISIONS
              </button>
              <button
                onClick={onApprove}
                className={`px-2 py-1 ${judgeResult.score >= judgeThreshold ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' : 'bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30'} rounded transition-colors text-[10px] font-bold`}
                title={judgeResult.score >= judgeThreshold ? 'Score meets threshold - approve and save final document' : 'Score below threshold - manually approve anyway and save final document'}
              >
                <Check className="w-3 h-3 inline mr-1" />
                APPROVE
              </button>
            </>
          )}

          {judgeResult && (
            <>
              <button
                onClick={onCopy}
                className="p-1 hover:bg-background-tertiary rounded transition-colors"
                title="Copy to clipboard"
              >
                <Copy className="w-3 h-3" />
              </button>
              <button
                onClick={onInflate}
                className="p-1 hover:bg-background-tertiary rounded transition-colors"
                title="Inflate to Markdown"
              >
                <FileDown className="w-3 h-3" />
              </button>
              <button
                onClick={onSave}
                className="p-1 hover:bg-background-tertiary rounded transition-colors"
                title="Save as MOTH"
              >
                <Save className="w-3 h-3" />
              </button>
              <button
                onClick={onSendToOrchestrator}
                className="p-1 hover:bg-background-tertiary rounded transition-colors"
                title="Send to Orchestrator"
              >
                <ArrowLeft className="w-3 h-3" />
              </button>
            </>
          )}

          <button
            onClick={onExpand}
            className="p-1 hover:bg-background-tertiary rounded transition-colors"
            title="Expand"
          >
            <Maximize2 className="w-3 h-3" />
          </button>
        </div>
      </div>

      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Scrollable Content */}
        <div className="flex-1 p-3 text-xs overflow-auto">
          {judgeResult ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="text-lg font-bold">Score: {judgeResult.score.toFixed(2)}</div>
                <div className={`px-2 py-0.5 rounded text-[10px] font-bold ${judgeResult.score >= judgeThreshold ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                  {judgeResult.score >= judgeThreshold ? 'APPROVED' : 'NEEDS WORK'}
                </div>
              </div>
              <p className="whitespace-pre-wrap font-mono text-foreground-secondary">{judgeResult.reasoning}</p>
            </div>
          ) : isOrchestrating && allPeersResponded ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="text-yellow-400 animate-pulse text-4xl mb-2">●</div>
                <p className="text-foreground-muted text-xs">Evaluating peer responses...</p>
              </div>
            </div>
          ) : (
            <p className="text-foreground-muted italic">No judgment yet</p>
          )}
        </div>

        {/* Notes Section removed from sidebar - only available in expanded view */}

        {/* Resize Handle */}
        <div
          className="h-2 cursor-row-resize bg-border/30 hover:bg-accent/40 active:bg-accent/60 transition-colors flex items-center justify-center"
          onMouseDown={(e) => {
            e.preventDefault()
            onResizeStart()
          }}
          title="Drag to resize"
        >
          <div className="w-8 h-0.5 bg-foreground-muted/40 rounded-full" />
        </div>
      </div>
    </div>
  )
}

